import { Component } from '@theme/component';
import { hasActiveScrollLockConsumers } from '@theme/dialog';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupSubmenuResetOnClose();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (this.isOpen) {
      this.close();
    } else {
      event?.preventDefault?.();
      this.open(event);
    }
  }

  /**
   * Open the closest drawer or the main menu drawer.
   * Applies open state and menu-open class synchronously so the drawer opens instantly.
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    details.classList.add('menu-open');

    document.documentElement.setAttribute('scroll-lock', '');

    this.preventInitialAccordionAnimations(details);

    setTimeout(() => {
      trapFocus(details);
    }, 0);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    const animatedElements = [
      details.querySelector('.menu-drawer'),
      details.querySelector('.menu-drawer__backdrop'),
    ].filter((element) => element instanceof HTMLElement);

    onAnimationEnd(animatedElements.length > 0 ? animatedElements : details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        if (!hasActiveScrollLockConsumers()) {
          document.documentElement.removeAttribute('scroll-lock');
        }
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * When a submenu opens: ensure first accordion is open on all screens (first or second time).
   * When a submenu closes: reset scroll and accordions, then open first accordion for next time.
   */
  #setupSubmenuResetOnClose() {
    const menuItemDetails = this.querySelectorAll('.mobile-menu-item-wrapper > details');
    menuItemDetails.forEach((details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      details.addEventListener('toggle', () => {
        const submenu = details.querySelector('.menu-drawer__submenu');
        if (!submenu) return;
        if (details.open) {
          ensureFirstAccordionOpen(details);
        } else {
          resetSubmenuContent(details);
        }
      });
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/**
 * Ensure the first accordion in the submenu is open. Used when submenu opens so first accordion
 * is always open on all screen sizes, first time or second time.
 *
 * @param {HTMLDetailsElement} menuItemDetails - The details element that wraps the submenu (menu item)
 */
function ensureFirstAccordionOpen(menuItemDetails) {
  const submenuPanel = menuItemDetails.querySelector('.menu-drawer__submenu');
  if (!submenuPanel) return;
  const firstAccordion = submenuPanel.querySelector('details');
  if (firstAccordion) {
    firstAccordion.setAttribute('open', '');
    firstAccordion.querySelector('summary')?.setAttribute('aria-expanded', 'true');
  }
}

/**
 * Reset submenu panel content when it closes: scroll to top, close all accordions, open first.
 * Next time the submenu opens it shows like the first time on all screens.
 *
 * @param {HTMLDetailsElement} menuItemDetails - The details element that wraps the submenu (menu item)
 */
function resetSubmenuContent(menuItemDetails) {
  const submenuPanel = menuItemDetails.querySelector('.menu-drawer__submenu');
  if (!submenuPanel) return;

  const scrollContainer = submenuPanel.querySelector('.menu-drawer__inner-submenu');
  if (scrollContainer) {
    scrollContainer.scrollTop = 0;
  }

  const allAccordionDetails = submenuPanel.querySelectorAll('details');
  allAccordionDetails.forEach((detailsEl) => {
    detailsEl.removeAttribute('open');
    detailsEl.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  });

  const firstAccordion = allAccordionDetails[0];
  if (firstAccordion) {
    firstAccordion.setAttribute('open', '');
    firstAccordion.querySelector('summary')?.setAttribute('aria-expanded', 'true');
  }
}
